import { ClockCircleOutlined } from "@ant-design/icons";
import Tooltip from "antd/es/tooltip";
import DateRenderer from "components/DateRenderer";
import { FC, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  formatTimezoneDifference,
  getLocalTimezoneId,
  getTimezoneDifferenceAtUtcTimestamp,
  isValidTimezoneId,
  toTimezoneWallClock,
} from "utils/timezoneUtils";

import styles from "./styles.module.less";

interface EventLocationDateTimeProps {
  date: Date | string;
  timezoneId?: string | null;
}

interface EventLocationTimeIndicatorProps {
  date?: Date | string | null;
  timezoneId?: string | null;
}

const loggedTimezoneFallbacks = new Set<string>();

const logTimezoneFallback = (timezoneId?: string | null) => {
  const timezoneKey = timezoneId || "missing";

  if (loggedTimezoneFallbacks.has(timezoneKey)) {
    return;
  }

  loggedTimezoneFallbacks.add(timezoneKey);
  // There is no application telemetry wrapper in the frontend. Log once per
  // mapping and session so invalid forum data is visible without flooding logs.
  // eslint-disable-next-line no-console
  console.warn(
    `[ELT] Forum timezone ${
      timezoneId ? `"${timezoneId}" is invalid` : "is missing"
    }; attendee status timestamps are displayed in UTC.`,
  );
};

export const EventLocationTimeIndicator: FC<EventLocationTimeIndicatorProps> = ({ date, timezoneId }) => {
  const { t } = useTranslation();
  const timezoneInterpolation = { interpolation: { escapeValue: false } };
  const localTimezoneId = useMemo(() => getLocalTimezoneId(), []);
  const hasEventTimezone = useMemo(() => isValidTimezoneId(timezoneId), [timezoneId]);
  const effectiveTimezoneId = hasEventTimezone ? timezoneId : "UTC";
  const referenceDate = useMemo(() => date ?? new Date(), [date]);
  const difference = useMemo(
    () =>
      formatTimezoneDifference(
        getTimezoneDifferenceAtUtcTimestamp(effectiveTimezoneId, localTimezoneId, referenceDate),
      ),
    [effectiveTimezoneId, localTimezoneId, referenceDate],
  );
  const localTimezoneSuffix = localTimezoneId ? ` (${localTimezoneId})` : "";
  const timezoneMessage = hasEventTimezone
    ? t("Displayed in Event Location Time (ELT): {{timezone}}", {
        timezone: timezoneId,
        ...timezoneInterpolation,
      })
    : t("Event location timezone is unavailable. Displayed in UTC.");
  const differenceMessage = `${t("Difference from your timezone{{localTimezone}}:", {
    localTimezone: localTimezoneSuffix,
    ...timezoneInterpolation,
  })} ${difference}`;
  const accessibleTooltip = `${timezoneMessage} ${differenceMessage}`;

  useEffect(() => {
    if (!hasEventTimezone) {
      logTimezoneFallback(timezoneId);
    }
  }, [hasEventTimezone, timezoneId]);

  return (
    <Tooltip
      title={
        <span className={styles.tooltipContent}>
          <span>{timezoneMessage}</span>
          <span>{differenceMessage}</span>
        </span>
      }
    >
      <button type="button" className={styles.indicator} aria-label={accessibleTooltip}>
        <ClockCircleOutlined />
        <span className={styles.label}>{hasEventTimezone ? t("ELT") : "UTC"}</span>
      </button>
    </Tooltip>
  );
};

const EventLocationDateTime: FC<EventLocationDateTimeProps> = ({ date, timezoneId }) => {
  const hasEventTimezone = useMemo(() => isValidTimezoneId(timezoneId), [timezoneId]);
  const effectiveTimezoneId = hasEventTimezone ? timezoneId : "UTC";
  const displayDate = useMemo(() => toTimezoneWallClock(date, effectiveTimezoneId), [date, effectiveTimezoneId]);

  return (
    <span className={styles.dateTime}>
      <DateRenderer date={displayDate ?? date} showTime wallClock />
    </span>
  );
};

export default EventLocationDateTime;
